import { type FC } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import PieChartComponent from './PieChart/PieChart';
import BarChartComponent from './BarChart/BarChart';
import LineChartComponent from './LineChart/LineChart';

export type ChartComponentDataEntry = {
    value: number;
    color?: string;
    label?: string;
};

export type ChartComponentData = Partial<Record<string, unknown>> & {
    name: string;
    values?: ChartComponentDataEntry[];
};

interface ChartComponentProps {
    chartType?: string;
    chartData?: ChartComponentData[];
    showLegend?: boolean;
}

export const DEFAULT_CHART_COLOR = '#808080';

const ChartComponent: FC<ChartComponentProps> = props => {
    const {
        chartType,
        chartData,
        showLegend = true,
    } = props;

    if (!chartType || !chartData) {
        return null;
    }

    const chartKeys: string[] = [];

    let chartValueLabel: string | undefined;
    const transformedChartData = chartData?.map(data => {
        let newData: Partial<Record<string, unknown>> = { name: data.name };

        let firstValue = null;
        let firstLabel = null;
        let firstColor = null;
        const subData: Partial<Record<string, unknown>> = {};

        data.values?.forEach((dataValue, index) => {
            if (index === 0) {
                firstValue = dataValue.value;
                firstLabel = dataValue.label;
                firstColor = dataValue.color;
            }

            if (dataValue.label && !chartKeys.includes(dataValue.label)) {
                chartKeys.push(dataValue.label);
            }

            subData[`${dataValue.label}`] = dataValue.value;
            subData[`${dataValue.label}-color`] = dataValue.color;
        });

        if (chartType === 'pie') {
            newData.value = firstValue;
            newData.label = firstLabel;
            newData.color = firstColor;
        } else {
            newData = { ...newData, ...subData };
        }

        return newData;
    });

    const unknownChartType = (chartType !== 'pie' && chartType !== 'bar' && chartType !== 'line');

    return (
        <div className="chart-component">
            {chartType === 'pie' && (
                <PieChartComponent
                    chartData={transformedChartData}
                    chartValueLabel={chartValueLabel}
                    showLegend={showLegend}
                />
            )}
            {chartType === 'bar' && (
                <BarChartComponent
                    chartData={transformedChartData}
                    chartKeys={chartKeys}
                    showLegend={showLegend}
                />
            )}
            {chartType === 'line' && (
                <LineChartComponent
                    chartData={transformedChartData}
                    chartKeys={chartKeys}
                    showLegend={showLegend}
                />
            )}
            {unknownChartType && (
                <p>Unknown chart type: {chartType}</p>
            )}
        </div>
    );
};

interface ChartComponentInitProps {
    el: HTMLDivElement;
    props: ChartComponentProps;
}

const ChartComponentInit = ({ el, props }: ChartComponentInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        // eslint-disable-next-line react/jsx-props-no-spreading
        <ChartComponent {...props} />
    );
};

export default ChartComponentInit;
